import React, { useMemo, useEffect, useRef, useState } from 'react';
import { DetectedPattern, PATTERN_FULL_NAME } from '../../../utils/chartPatternDetector';

// ─── Types ───────────────────────────────────────────────────────────────────

interface OptionRow {
  strike: number;
  callOI: number; putOI: number;
  callVolume: number; putVolume: number;
  callLTP: number; putLTP: number;
}

interface OILevel {
  level: number;
  type: 'oi_support' | 'oi_resistance' | 'max_pain';
  label: string;
  oi?: number;
}

interface Signal {
  name: string;
  value: string;
  score: number; // +1 bull, -1 bear, 0 neutral
  dir: 'bull' | 'bear' | 'neutral';
  thr?: string; // threshold hint shown inline
}

interface Strategy {
  name: string;
  type: 'bull' | 'bear' | 'neutral' | 'volatile';
  tag: string;
  legs: string[];
  sl: string;
  target: string;
  rationale: string;
}

interface OIChangeTotals {
  callChange: number;
  putChange: number;
}

interface StrategySignalPanelProps {
  symbol: string;
  spotPrice: number;
  tech: any;
  optionChain: OptionRow[];
  pcr: number;
  maxPain: number;
  oiLevels: OILevel[];
  onSignalChange?: (signal: { type: 'CE' | 'PE'; score: number } | null) => void;
  chartPatterns?: DetectedPattern[];
  oiChangeTotals?: OIChangeTotals | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtOI = (v: number) => {
  if (v >= 1e7) return (v / 1e7).toFixed(1) + 'Cr';
  if (v >= 1e5) return (v / 1e5).toFixed(1) + 'L';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return String(v);
};

const fmtOISigned = (v: number) => `${v >= 0 ? '+' : '-'}${fmtOI(Math.abs(v))}`;

const STEP: Record<string, number> = { NIFTY: 50, SENSEX: 100, FINNIFTY: 50 };
const getStep = (symbol: string) => STEP[symbol.toUpperCase()] ?? 50;
const atm = (spot: number, step: number) => Math.round(spot / step) * step;

// ─── Signal Scoring Engine ────────────────────────────────────────────────────
// 14 signals total (13 directional + BB Squeeze which is neutral)
// Max score = ±13  Thresholds: strong = ±6, moderate = ±3

const computeSignals = (
  tech: any,
  pcr: number,
  maxPain: number,
  spotPrice: number,
  optionChain: OptionRow[],
  step: number,
  oiChangeTotals?: OIChangeTotals | null
): Signal[] => {
  const signals: Signal[] = [];
  if (!tech || !spotPrice) return signals;

  const adxVal = tech.adx?.value ?? 0;

  // 1. RSI 14 — standard thresholds: <35 oversold, >65 overbought
  const rsi = tech.rsi?.value;
  if (rsi != null) {
    if (rsi < 35) signals.push({ name: 'RSI', value: `${rsi.toFixed(1)} oversold`, score: 1, dir: 'bull', thr: '<35' });
    else if (rsi > 65) signals.push({ name: 'RSI', value: `${rsi.toFixed(1)} overbought`, score: -1, dir: 'bear', thr: '>65' });
    else signals.push({ name: 'RSI', value: `${rsi.toFixed(1)} neutral`, score: 0, dir: 'neutral', thr: 'fire <35/>65' });
  }

  // 2. MACD histogram
  const hist = tech.macd?.histogram;
  const macdSig = tech.macd?.signal;
  if (hist != null) {
    if (hist > 0 && macdSig === 'bullish') signals.push({ name: 'MACD', value: `▲ ${hist.toFixed(1)}`, score: 1, dir: 'bull', thr: 'hist+sig' });
    else if (hist < 0 && macdSig === 'bearish') signals.push({ name: 'MACD', value: `▼ ${hist.toFixed(1)}`, score: -1, dir: 'bear', thr: 'hist+sig' });
    else signals.push({ name: 'MACD', value: `${hist > 0 ? '+' : ''}${hist.toFixed(1)} neutral`, score: 0, dir: 'neutral', thr: 'need hist+sig agree' });
  }

  // 3. MA Signal — use backend composite ma_signal (all MAs), fallback to SMA50
  const maSig = tech.moving_averages?.signal;
  const sma50 = tech.moving_averages?.sma_50;
  if (maSig && maSig !== 'neutral') {
    const pctCtx = sma50 ? ` (SMA50 ${((spotPrice - sma50) / sma50 * 100) > 0 ? '+' : ''}${((spotPrice - sma50) / sma50 * 100).toFixed(1)}%)` : '';
    if (maSig === 'bullish') signals.push({ name: 'MA Signal', value: `bullish${pctCtx}`, score: 1, dir: 'bull', thr: 'backend' });
    else signals.push({ name: 'MA Signal', value: `bearish${pctCtx}`, score: -1, dir: 'bear', thr: 'backend' });
  } else if (sma50 && sma50 > 0) {
    const pct = ((spotPrice - sma50) / sma50) * 100;
    if (pct > 0.2) signals.push({ name: 'MA Signal', value: `above SMA50 +${pct.toFixed(1)}%`, score: 1, dir: 'bull', thr: '>0.2%' });
    else if (pct < -0.2) signals.push({ name: 'MA Signal', value: `below SMA50 ${pct.toFixed(1)}%`, score: -1, dir: 'bear', thr: '<-0.2%' });
    else signals.push({ name: 'MA Signal', value: `near SMA50`, score: 0, dir: 'neutral', thr: 'fire >±0.2%' });
  }

  // 4. PCR (OI-based, contrarian)
  if (pcr > 0) {
    if (pcr > 1.15) signals.push({ name: 'PCR', value: `${pcr.toFixed(2)} put floor`, score: 1, dir: 'bull', thr: '>1.15' });
    else if (pcr < 0.75) signals.push({ name: 'PCR', value: `${pcr.toFixed(2)} call ceiling`, score: -1, dir: 'bear', thr: '<0.75' });
    else signals.push({ name: 'PCR', value: `${pcr.toFixed(2)} neutral`, score: 0, dir: 'neutral', thr: 'fire <0.75/>1.15' });
  }

  // 5. BB position — only signal mean-reversion in range-bound market (ADX < 25)
  // In a trend, upper/lower band touch is trend continuation, not reversal
  const bbU = tech.bollinger_bands?.upper;
  const bbL = tech.bollinger_bands?.lower;
  if (bbU && bbL && (bbU - bbL) > 0) {
    const pos = ((spotPrice - bbL) / (bbU - bbL)) * 100;
    const isTrending = adxVal > 25;
    if (!isTrending && pos <= 20) signals.push({ name: 'BB Pos', value: `near lower ${pos.toFixed(0)}%`, score: 1, dir: 'bull', thr: '<20% no-trend' });
    else if (!isTrending && pos >= 80) signals.push({ name: 'BB Pos', value: `near upper ${pos.toFixed(0)}%`, score: -1, dir: 'bear', thr: '>80% no-trend' });
    else signals.push({ name: 'BB Pos', value: `${isTrending ? 'trend ' : 'mid '}${pos.toFixed(0)}%`, score: 0, dir: 'neutral', thr: isTrending ? 'ADX>25→off' : 'fire <20%/>80%' });
  }

  // 6. Max Pain gravity
  if (maxPain > 0) {
    const diff = spotPrice - maxPain;
    const thresh = maxPain * 0.008; // 0.8% — tight enough to matter, wide enough not to always fire
    if (diff < -thresh) signals.push({ name: 'MaxPain', value: `${maxPain} ↑ gravity`, score: 1, dir: 'bull', thr: '>0.8% below' });
    else if (diff > thresh) signals.push({ name: 'MaxPain', value: `${maxPain} ↓ gravity`, score: -1, dir: 'bear', thr: '>0.8% above' });
    else signals.push({ name: 'MaxPain', value: `pinned @ ${maxPain}`, score: 0, dir: 'neutral', thr: 'fire >±0.8%' });
  }

  // 7. Wall Proximity — is spot within 1% of the strongest CE or PE wall?
  // Replaces OI Flow which was a duplicate of PCR (both measure total put vs call OI ratio)
  if (optionChain.length > 0) {
    const aboveSpot = optionChain.filter(r => r.strike > spotPrice);
    const belowSpot = optionChain.filter(r => r.strike <= spotPrice);
    const topCEStrike = aboveSpot.length ? aboveSpot.reduce((b, r) => r.callOI > b.callOI ? r : b).strike : null;
    const topPEStrike = belowSpot.length ? belowSpot.reduce((b, r) => r.putOI > b.putOI ? r : b).strike : null;
    if (topCEStrike && topPEStrike) {
      const ceDist = (topCEStrike - spotPrice) / spotPrice * 100;
      const peDist = (spotPrice - topPEStrike) / spotPrice * 100;
      const ceNear = ceDist < 1.0;
      const peNear = peDist < 1.0;
      if (ceNear && peNear) signals.push({ name: 'Wall', value: `trapped CE ${topCEStrike}/PE ${topPEStrike}`, score: 0, dir: 'neutral', thr: '<1% both' });
      else if (ceNear) signals.push({ name: 'Wall', value: `at CE wall ${topCEStrike} (${ceDist.toFixed(1)}%)`, score: -1, dir: 'bear', thr: '<1% CE' });
      else if (peNear) signals.push({ name: 'Wall', value: `at PE wall ${topPEStrike} (${peDist.toFixed(1)}%)`, score: 1, dir: 'bull', thr: '<1% PE' });
      else signals.push({ name: 'Wall', value: `CE ${topCEStrike} | PE ${topPEStrike} open`, score: 0, dir: 'neutral', thr: `CE ${ceDist.toFixed(1)}% PE ${peDist.toFixed(1)}%` });
    }
  }

  // 8. Stochastic K/D
  const sk = tech.stochastic?.k;
  const sd_val = tech.stochastic?.d;
  if (sk != null && sd_val != null) {
    if (sk < 25 && sd_val < 25) signals.push({ name: 'Stoch', value: `K${sk.toFixed(0)} D${sd_val.toFixed(0)} oversold`, score: 1, dir: 'bull', thr: 'K&D<25' });
    else if (sk > 75 && sd_val > 75) signals.push({ name: 'Stoch', value: `K${sk.toFixed(0)} D${sd_val.toFixed(0)} overbought`, score: -1, dir: 'bear', thr: 'K&D>75' });
    else signals.push({ name: 'Stoch', value: `K${sk.toFixed(0)} D${sd_val.toFixed(0)}`, score: 0, dir: 'neutral', thr: 'fire K&D<25/>75' });
  }

  // ── NEW SIGNALS ───────────────────────────────────────────────────────────

  // 9. EMA Cross (EMA20 vs EMA50) — golden/death cross
  const ema20 = tech.moving_averages?.ema_20;
  const ema50 = tech.moving_averages?.ema_50;
  if (ema20 && ema50 && ema20 > 0 && ema50 > 0) {
    const cross = ((ema20 - ema50) / ema50) * 100;
    if (cross > 0.5) signals.push({ name: 'EMA Cross', value: `EMA20 > EMA50 +${cross.toFixed(2)}%`, score: 1, dir: 'bull', thr: '>0.5%' });
    else if (cross < -0.5) signals.push({ name: 'EMA Cross', value: `EMA20 < EMA50 ${cross.toFixed(2)}%`, score: -1, dir: 'bear', thr: '<-0.5%' });
    else signals.push({ name: 'EMA Cross', value: `crossing ${cross.toFixed(2)}%`, score: 0, dir: 'neutral', thr: 'fire >±0.5%' });
  }

  // 10. Price vs Daily Pivot Point
  const pivot = tech.pivot_points?.pivot;
  if (pivot && pivot > 0) {
    const pct = ((spotPrice - pivot) / pivot) * 100;
    if (pct > 0.5) signals.push({ name: 'Pivot', value: `above PP +${pct.toFixed(2)}%`, score: 1, dir: 'bull', thr: '>0.5%' });
    else if (pct < -0.5) signals.push({ name: 'Pivot', value: `below PP ${pct.toFixed(2)}%`, score: -1, dir: 'bear', thr: '<-0.5%' });
    else signals.push({ name: 'Pivot', value: `at PP ${pivot.toLocaleString('en-IN')}`, score: 0, dir: 'neutral', thr: 'fire >±0.5%' });
  }

  // 11. BB Squeeze — band width compression (score = 0, used to flip strategy to volatile)
  const bbM = tech.bollinger_bands?.middle;
  if (bbU && bbL && bbM && bbM > 0) {
    const width = ((bbU - bbL) / bbM) * 100;
    if (width < 1.5) signals.push({ name: 'BB Squeeze', value: `tight ${width.toFixed(1)}% → breakout`, score: 0, dir: 'neutral', thr: '<1.5% squeeze' });
    else if (width > 4) signals.push({ name: 'BB Width', value: `wide ${width.toFixed(1)}% trending`, score: 0, dir: 'neutral', thr: '>4% wide' });
    else signals.push({ name: 'BB Width', value: `normal ${width.toFixed(1)}%`, score: 0, dir: 'neutral', thr: '1.5-4% normal' });
  }

  // 12. IV Skew — ATM CE LTP vs PE LTP (options-specific risk pricing)
  if (optionChain.length > 0) {
    const atmStrike = atm(spotPrice, step);
    const atmRow = optionChain.reduce<OptionRow | null>((best, r) => {
      if (!best) return r;
      return Math.abs(r.strike - atmStrike) < Math.abs(best.strike - atmStrike) ? r : best;
    }, null);
    if (atmRow && atmRow.callLTP > 0 && atmRow.putLTP > 0) {
      // % difference from midpoint — symmetric, avoids structural put bias of raw ratio
      // +10% means CE is 10% more expensive = upside demand; -10% means PE is 10% costlier = fear
      const mid = (atmRow.callLTP + atmRow.putLTP) / 2;
      const skewPct = ((atmRow.callLTP - atmRow.putLTP) / mid) * 100;
      if (skewPct > 10) signals.push({ name: 'IV Skew', value: `CE ${skewPct.toFixed(0)}% costlier — upside bid`, score: 1, dir: 'bull', thr: '>10%' });
      else if (skewPct < -10) signals.push({ name: 'IV Skew', value: `PE ${(-skewPct).toFixed(0)}% costlier — fear`, score: -1, dir: 'bear', thr: '<-10%' });
      else signals.push({ name: 'IV Skew', value: `balanced ${skewPct.toFixed(1)}%`, score: 0, dir: 'neutral', thr: 'fire >±10%' });
    }
  }

  // 13. PCVR — Put-Call Volume Ratio (intraday, faster than OI-PCR)
  if (optionChain.length > 0) {
    const totalCEVol = optionChain.reduce((s, r) => s + (r.callVolume || 0), 0);
    const totalPEVol = optionChain.reduce((s, r) => s + (r.putVolume || 0), 0);
    if (totalCEVol + totalPEVol > 100) {
      const pcvr = totalPEVol / (totalCEVol || 1);
      // Directional (not contrarian) — volume reveals what traders are actively doing this session
      // High put volume = traders buying puts for downside = bearish activity
      // High call volume = traders buying calls for upside = bullish activity
      // Complements PCR (OI-based, contrarian) — they can diverge, giving richer context
      if (pcvr > 1.2) signals.push({ name: 'PCVR', value: `${pcvr.toFixed(2)} put vol dominance`, score: -1, dir: 'bear', thr: '>1.2' });
      else if (pcvr < 0.8) signals.push({ name: 'PCVR', value: `${pcvr.toFixed(2)} call vol dominance`, score: 1, dir: 'bull', thr: '<0.8' });
      else signals.push({ name: 'PCVR', value: `${pcvr.toFixed(2)} balanced`, score: 0, dir: 'neutral', thr: 'fire <0.8/>1.2' });
    }
  }

  // 14. OI Buildup — is fresh writing happening on the put side or call side RIGHT NOW?
  // Unlike PCR (a snapshot ratio of the whole day's accumulated OI), this looks at the
  // change since the last snapshot (~5 min) — distinguishes fresh conviction from stale OI.
  if (oiChangeTotals) {
    const { callChange, putChange } = oiChangeTotals;
    const totalAbsChange = Math.abs(callChange) + Math.abs(putChange);
    if (totalAbsChange > 0) {
      const putShare = (putChange - callChange) / totalAbsChange; // -1..1, +ve = put-side buildup
      if (putShare > 0.3) signals.push({ name: 'OI Buildup', value: `Put OI ${fmtOISigned(putChange)} — floor building`, score: 1, dir: 'bull', thr: 'putShare>0.3' });
      else if (putShare < -0.3) signals.push({ name: 'OI Buildup', value: `Call OI ${fmtOISigned(callChange)} — ceiling building`, score: -1, dir: 'bear', thr: 'putShare<-0.3' });
      else signals.push({ name: 'OI Buildup', value: `CE ${fmtOISigned(callChange)} / PE ${fmtOISigned(putChange)} balanced`, score: 0, dir: 'neutral', thr: 'fire >±0.3 share' });
    }
  }

  return signals;
};

// ─── Strategy Selection Engine ────────────────────────────────────────────────
// Thresholds updated for 14-signal scale (max ±13):
//   strong = ±6  moderate = ±3  neutral = -2 to +2

const selectStrategies = (
  score: number,
  adx: number,
  adxSig: string,
  atrSig: string,
  spotPrice: number,
  maxPain: number,
  oiLevels: OILevel[],
  step: number,
  bbSqueeze: boolean
): Strategy[] => {
  const strategies: Strategy[] = [];
  const a = atm(spotPrice, step);

  const trending = adxSig === 'strong_trend' || adx > 25;
  const rangebound = adxSig === 'no_trend' || adx < 18;
  const highVol = atrSig === 'high' || bbSqueeze; // squeeze = breakout = treat as volatile

  const ceWalls = oiLevels.filter(l => l.type === 'oi_resistance').sort((x, y) => (y.oi ?? 0) - (x.oi ?? 0));
  const peWalls = oiLevels.filter(l => l.type === 'oi_support').sort((x, y) => (y.oi ?? 0) - (x.oi ?? 0));
  const topCE = ceWalls[0]?.level;
  const topPE = peWalls[0]?.level;
  const secCE = ceWalls[1]?.level;
  const secPE = peWalls[1]?.level;

  const nearCE = topCE != null && Math.abs(spotPrice - topCE) / spotPrice < 0.003;
  const nearPE = topPE != null && Math.abs(spotPrice - topPE) / spotPrice < 0.003;
  const nearMP = maxPain > 0 && Math.abs(spotPrice - maxPain) / maxPain < 0.004;

  // ── Special Context Overrides ──────────────────────────────────────────────

  if (nearCE && score <= 0) {
    strategies.push({
      name: 'Resistance Rejection',
      type: 'bear', tag: 'CONTEXT PLAY',
      legs: [`Buy ${a} PE`],
      sl: `CE Wall ${topCE} break above`,
      target: topPE ? `PE Wall ${topPE}` : `${a - step * 2}`,
      rationale: `Price at CE Wall ${topCE} — massive call writing = strong resistance`,
    });
  }

  if (nearPE && score >= 0) {
    strategies.push({
      name: 'Support Bounce',
      type: 'bull', tag: 'CONTEXT PLAY',
      legs: [`Buy ${a} CE`],
      sl: `PE Wall ${topPE} break below`,
      target: topCE ? `CE Wall ${topCE}` : `${a + step * 2}`,
      rationale: `Price at PE Wall ${topPE} — massive put writing = strong floor`,
    });
  }

  if (nearMP && !highVol && rangebound) {
    strategies.push({
      name: 'Max Pain Pin',
      type: 'neutral', tag: 'EXPIRY PLAY',
      legs: [`Sell ${maxPain} CE`, `Sell ${maxPain} PE`],
      sl: `Price moves > 0.5% from ${maxPain}`,
      target: `Theta decay — both legs expire worthless`,
      rationale: `Price pinned near Max Pain — time decay favours sellers`,
    });
  }

  // ── Main Score-based Logic ─────────────────────────────────────────────────

  if (score >= 6) {
    if (trending) {
      strategies.push({
        name: 'Long ATM CE',
        type: 'bull', tag: 'MOMENTUM',
        legs: [`Buy ${a} CE`],
        sl: topPE ? `PE Wall ${topPE} break` : `${a - step * 2} break`,
        target: topCE ? `CE Wall ${topCE}` : `${a + step * 3}`,
        rationale: `Strong bull momentum (ADX ${adx.toFixed(0)}, ${score} of 14 signals) — ride the trend`,
      });
      strategies.push({
        name: 'Bull Call Spread',
        type: 'bull', tag: 'BEST FIT',
        legs: [`Buy ${a} CE`, `Sell ${a + step} CE`],
        sl: `${a - step} spot close below`,
        target: `${a + step} (full spread profit)`,
        rationale: `Risk-defined — lower premium outlay, capped on both sides`,
      });
    } else {
      strategies.push({
        name: 'Bull Call Spread',
        type: 'bull', tag: 'BEST FIT',
        legs: [`Buy ${a} CE`, `Sell ${a + step} CE`],
        sl: `${a - step} spot close below`,
        target: `${a + step}`,
        rationale: `Strong bull bias (${score}/13) but trend unconfirmed — defined risk safer`,
      });
      strategies.push({
        name: `Short ${a - step} PE`,
        type: 'bull', tag: 'PREMIUM',
        legs: [`Sell ${a - step} PE`],
        sl: `${a - step * 2} spot close below`,
        target: `PE expires worthless — full premium`,
        rationale: `Collect premium — put writers profit if price holds above`,
      });
    }
  } else if (score >= 3) {
    strategies.push({
      name: 'Bull Call Spread',
      type: 'bull', tag: 'MODERATE',
      legs: [`Buy ${a} CE`, `Sell ${a + step} CE`],
      sl: `${a - step} spot break`,
      target: `${a + step}`,
      rationale: `Mild bullish bias (${score}/13) — spread limits cost and risk`,
    });
    strategies.push({
      name: 'Short OTM PE',
      type: 'bull', tag: 'PREMIUM',
      legs: [`Sell ${a - step} PE`],
      sl: `${a - step * 2} spot break`,
      target: `Full premium on expiry`,
      rationale: `Low-cost premium collection with upside bias`,
    });
  } else if (score >= -2) {
    // Neutral zone — high vol / BB squeeze → long volatility; else → sell volatility
    if (highVol) {
      const reason = bbSqueeze ? 'BB squeeze → breakout imminent' : 'High ATR — large move expected';
      strategies.push({
        name: 'Long Straddle',
        type: 'volatile', tag: 'BREAKOUT',
        legs: [`Buy ${a} CE`, `Buy ${a} PE`],
        sl: `Flat > 2 sessions (time decay kills)`,
        target: `CE Wall ${topCE ?? a + step * 3} OR PE Wall ${topPE ?? a - step * 3}`,
        rationale: `${reason}, direction unclear — one leg profits`,
      });
      strategies.push({
        name: 'Long Strangle',
        type: 'volatile', tag: 'CHEAPER',
        legs: [`Buy ${a - step} PE`, `Buy ${a + step} CE`],
        sl: `Flat > 2 sessions`,
        target: `Either wing reaches OI wall`,
        rationale: `Cheaper than straddle — needs bigger move but lower upfront cost`,
      });
    } else {
      const icLP = secPE ? secPE - step : a - step * 3;
      const icSP = topPE ? topPE - step : a - step * 2;
      const icSC = topCE ? topCE + step : a + step * 2;
      const icLC = secCE ? secCE + step : a + step * 3;
      strategies.push({
        name: 'Short Straddle',
        type: 'neutral', tag: 'RANGE PLAY',
        legs: [`Sell ${maxPain || a} CE`, `Sell ${maxPain || a} PE`],
        sl: `Move > 1% from ${maxPain || a}`,
        target: `Both legs expire — collect full premium`,
        rationale: `Sideways market (${score}/13 neutral) — sell volatility near Max Pain`,
      });
      strategies.push({
        name: 'Iron Condor',
        type: 'neutral', tag: 'SAFEST',
        legs: [`Long ${icLP} PE`, `Short ${icSP} PE`, `Short ${icSC} CE`, `Long ${icLC} CE`],
        sl: `Break of ${icSP} or ${icSC}`,
        target: `Range ${icSP}–${icSC} — max profit inside`,
        rationale: `Defined-risk range play — OI walls set the boundaries`,
      });
    }
  } else if (score <= -6) {
    if (trending) {
      strategies.push({
        name: 'Long ATM PE',
        type: 'bear', tag: 'MOMENTUM',
        legs: [`Buy ${a} PE`],
        sl: topCE ? `CE Wall ${topCE} break` : `${a + step * 2} break`,
        target: topPE ? `PE Wall ${topPE}` : `${a - step * 3}`,
        rationale: `Strong bear momentum (ADX ${adx.toFixed(0)}, ${score} of 14 signals)`,
      });
      strategies.push({
        name: 'Bear Put Spread',
        type: 'bear', tag: 'BEST FIT',
        legs: [`Buy ${a} PE`, `Sell ${a - step} PE`],
        sl: `${a + step} spot close above`,
        target: `${a - step} (full spread profit)`,
        rationale: `Risk-defined bear play — lower cost, capped downside loss`,
      });
    } else {
      strategies.push({
        name: 'Bear Put Spread',
        type: 'bear', tag: 'BEST FIT',
        legs: [`Buy ${a} PE`, `Sell ${a - step} PE`],
        sl: `${a + step} spot break`,
        target: `${a - step}`,
        rationale: `Strong bear bias (${score}/13) but trend unconfirmed — defined risk safer`,
      });
      strategies.push({
        name: 'Short OTM CE',
        type: 'bear', tag: 'PREMIUM',
        legs: [`Sell ${a + step} CE`],
        sl: `${a + step * 2} spot close above`,
        target: `CE expires worthless — full premium`,
        rationale: `Collect premium with bearish bias`,
      });
    }
  } else if (score <= -3) {
    strategies.push({
      name: 'Bear Put Spread',
      type: 'bear', tag: 'MODERATE',
      legs: [`Buy ${a} PE`, `Sell ${a - step} PE`],
      sl: `${a + step} spot break`,
      target: `${a - step}`,
      rationale: `Mild bearish bias (${score}/13) — spread limits premium and risk`,
    });
    strategies.push({
      name: 'Short OTM CE',
      type: 'bear', tag: 'PREMIUM',
      legs: [`Sell ${a + step} CE`],
      sl: `${a + step * 2} spot break`,
      target: `Full premium on expiry`,
      rationale: `Premium collection with bearish bias`,
    });
  }

  return strategies.slice(0, 2);
};

// ─── Signal Descriptions ──────────────────────────────────────────────────────

const SIGNAL_DESCRIPTIONS: Record<string, { what: string; how: string; fire: string; caution: string }> = {
  RSI: {
    what: 'Relative Strength Index (14-period). Measures speed and magnitude of recent price changes.',
    how: 'Compares average gains vs losses over 14 candles. Ranges 0–100.',
    fire: 'Bull when RSI < 35 (oversold, bounce likely). Bear when RSI > 65 (overbought, pullback likely).',
    caution: 'In strong trends RSI can stay overbought/oversold for extended periods. Not standalone.',
  },
  MACD: {
    what: 'Moving Average Convergence Divergence. Measures momentum via two EMA crossovers.',
    how: 'Histogram = MACD line − Signal line. Fires only when histogram direction AND backend categorical signal agree.',
    fire: 'Bull when histogram > 0 AND signal = bullish. Bear when histogram < 0 AND signal = bearish.',
    caution: 'Requires BOTH conditions — prevents false signals when histogram barely crosses zero.',
  },
  'MA Signal': {
    what: 'Composite moving average signal from backend (all MAs weighted). Falls back to SMA50 vs spot.',
    how: 'Backend aggregates EMA9, EMA21, SMA20, SMA50, SMA200 into a single bullish/bearish/neutral signal.',
    fire: 'Bull when backend says bullish (or spot >0.2% above SMA50). Bear when bearish (or >0.2% below).',
    caution: 'Backend signal timing may lag by one candle. SMA50 fallback activates when backend returns neutral.',
  },
  PCR: {
    what: 'Put-Call Ratio (OI-based). Contrarian indicator — heavy put writing creates a price floor.',
    how: 'Total put OI / Total call OI across all strikes. Contrarian: high PCR = writers protecting downside = bull.',
    fire: 'Bull when PCR > 1.15 (put writers dominate → support). Bear when PCR < 0.75 (call writers → ceiling).',
    caution: 'Works best mid-expiry. Near expiry unwinding can flip PCR rapidly. PCR alone is not directional.',
  },
  'BB Pos': {
    what: 'Bollinger Band Position. Where is spot within the upper-lower band range?',
    how: 'pos = (spot − lower) / (upper − lower) × 100. ADX filter: suppressed if ADX > 25 (trending market).',
    fire: 'Bull when pos < 20% and ADX < 25 (mean-reversion bounce). Bear when pos > 80% and ADX < 25.',
    caution: 'DISABLED in trends (ADX > 25) — in a trend, touching upper band is continuation not reversal.',
  },
  MaxPain: {
    what: 'Max Pain gravity. Option writers profit most when spot expires at the strike with maximum put+call pain.',
    how: 'Computes the strike where sum of (all ITM option payoffs) is minimized. Gravity pulls spot toward it.',
    fire: 'Bull when spot is >0.8% below max pain (gravity up). Bear when >0.8% above (gravity down).',
    caution: 'Most reliable in expiry week. Early in cycle, writers have less incentive to pin the price.',
  },
  Wall: {
    what: 'OI Wall Proximity. Is spot approaching the strike with the highest CE or PE open interest?',
    how: 'Finds top CE OI strike above spot and top PE OI strike below spot. Measures % distance.',
    fire: 'Bear when within 1% of CE wall (massive call writing = resistance ceiling). Bull when within 1% of PE wall.',
    caution: 'Walls can shift intraday as OI is added/removed. Best used when spot is trending into the wall.',
  },
  Stoch: {
    what: 'Stochastic Oscillator (K and D lines). Compares closing price to recent high-low range.',
    how: 'K = current position within recent range. D = 3-period MA of K. Both must confirm for signal.',
    fire: 'Bull when K < 25 AND D < 25 (oversold). Bear when K > 75 AND D > 75 (overbought).',
    caution: 'Requires BOTH K and D in zone — single line crossing is insufficient. Lags in fast markets.',
  },
  'EMA Cross': {
    what: 'EMA 20 vs EMA 50 crossover (golden/death cross). Measures short-term vs medium-term trend.',
    how: 'cross% = (EMA20 − EMA50) / EMA50 × 100. A real cross needs meaningful separation, not just a tick.',
    fire: 'Bull when EMA20 > EMA50 by >0.5%. Bear when EMA20 < EMA50 by >0.5%. Neutral within ±0.5%.',
    caution: '0.5% threshold prevents false crosses at price. For NIFTY 24000 that is ~120 pts separation required.',
  },
  Pivot: {
    what: 'Daily Pivot Point. Calculated from previous day O/H/L/C. Acts as intraday support/resistance.',
    how: 'PP = (H + L + C) / 3 from previous session. Signal = how far spot is from pivot as %.',
    fire: 'Bull when spot > 0.5% above pivot. Bear when > 0.5% below pivot. ±0.5% zone is neutral.',
    caution: 'Pivot is fixed for the day. Morning gap-up/gap-down can put spot far from pivot at open — normal.',
  },
  'BB Squeeze': {
    what: 'Bollinger Band width compression. Tight bands = low volatility = energy coiling for a breakout.',
    how: 'width = (upper − lower) / middle × 100. Width < 1.5% = squeeze. Flips neutral strategy to long volatility.',
    fire: 'Neutral signal (score 0) but overrides strategy to Long Straddle/Strangle when active.',
    caution: 'Does not predict direction — only that a big move is imminent. Trade both sides until breakout confirms.',
  },
  'BB Width': {
    what: 'Bollinger Band width state. Shows current volatility regime relative to the band range.',
    how: 'width = (upper − lower) / middle × 100. Normal: 1.5-4%. Wide: >4% (trending). Squeeze: <1.5%.',
    fire: 'Neutral signal (score 0) for all states. Informational — used for strategy context.',
    caution: 'Wide bands in a strong trend are expected and healthy. Only squeeze is a strategy override signal.',
  },
  'IV Skew': {
    what: 'ATM Implied Volatility Skew via premium differential. Which option is more expensive at the ATM strike?',
    how: '(CE LTP − PE LTP) / midpoint × 100. Positive = CE more expensive (upside demand). Negative = PE (fear).',
    fire: 'Bull when CE is >10% costlier than PE at ATM. Bear when PE is >10% costlier. Neutral within ±10%.',
    caution: 'This is a LTP ratio proxy, not true IV. True IV requires Black-Scholes. Valid as relative demand signal.',
  },
  PCVR: {
    what: 'Put-Call Volume Ratio (intraday). Directional signal — high put volume = traders buying puts = fear.',
    how: 'Sum of all put volume / sum of all call volume across chain. Updates with each option chain refresh.',
    fire: 'Bear when PCVR > 1.2 (put vol heavy — traders loading puts). Bull when < 0.8 (call vol dominant).',
    caution: 'Directional, not contrarian. Complements PCR — PCR bull + PCVR bear = support but fresh fear too.',
  },
  'OI Buildup': {
    what: 'OI change since the last snapshot (~5 min) — is fresh writing happening on the put or call side right now?',
    how: 'Backend captures an OI snapshot every 5 min. This compares the two most recent snapshots directly, not the whole day’s accumulated OI.',
    fire: 'Bull when put OI is building faster than call OI (share > 0.3). Bear when call OI is building faster (share < -0.3).',
    caution: 'Unlike PCR (a stable full-day ratio), this is noisy and can flip within a session — treat as a "what is happening right now" signal, not a standalone call.',
  },
};

const SignalInfoModal: React.FC<{ name: string; onClose: () => void }> = ({ name, onClose }) => {
  const info = SIGNAL_DESCRIPTIONS[name];
  if (!info) return null;
  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#131722', border: '1px solid #2b2b43', borderRadius: 8, width: 380, padding: '1rem', maxHeight: '80vh', overflow: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
          <span style={{ color: '#d1d4dc', fontWeight: 800, fontSize: '0.85rem' }}>{name}</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#787b86', fontSize: '1rem', cursor: 'pointer' }}>✕</button>
        </div>
        {([['What', info.what], ['How it works', info.how], ['When it fires', info.fire], ['Watch out', info.caution]] as [string, string][]).map(([label, text]) => (
          <div key={label} style={{ marginBottom: '0.6rem' }}>
            <div style={{ fontSize: '0.58rem', color: '#787b86', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: '0.2rem' }}>{label}</div>
            <div style={{ fontSize: '0.68rem', color: '#d1d4dc', lineHeight: 1.5 }}>{text}</div>
          </div>
        ))}
        <div style={{ marginTop: '0.5rem', fontSize: '0.55rem', color: '#444', textAlign: 'right' }}>Click anywhere to close</div>
      </div>
    </div>
  );
};

// ─── Alert Storage ─────────────────────────────────────────────────────────────

const ALERT_KEY = 'mktpulse_alerts';
const loadAlertThreshold = (): number => {
  try { return parseInt(localStorage.getItem(ALERT_KEY) ?? '7', 10) || 7; } catch { return 7; }
};
const saveAlertThreshold = (v: number) => {
  try { localStorage.setItem(ALERT_KEY, String(v)); } catch { /* quota */ }
};

const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
};

const fireNotification = (title: string, body: string) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, { body, icon: '/favicon.ico' });
  }
};

// ─── Component ────────────────────────────────────────────────────────────────

const StrategySignalPanel: React.FC<StrategySignalPanelProps> = ({
  symbol, spotPrice, tech, optionChain, pcr, maxPain, oiLevels, onSignalChange,
  chartPatterns = [], oiChangeTotals = null,
}) => {
  const step = getStep(symbol);
  const adx = tech?.adx?.value ?? 0;
  const adxSig = tech?.adx?.signal ?? '';
  const atrSig = tech?.atr?.signal ?? '';

  const signals = useMemo(
    () => computeSignals(tech, pcr, maxPain, spotPrice, optionChain, step, oiChangeTotals),
    [tech, pcr, maxPain, spotPrice, optionChain, step, oiChangeTotals]
  );

  const signalScore = useMemo(() => signals.reduce((s, sig) => s + sig.score, 0), [signals]);

  // Pattern contribution: +scoreContribution for bull, -scoreContribution for bear; cap at ±4
  const patternScore = useMemo(() => {
    const raw = chartPatterns.reduce((s, p) => s + (p.signal === 'bull' ? p.scoreContribution : -p.scoreContribution), 0);
    return Math.max(-4, Math.min(4, raw));
  }, [chartPatterns]);

  const score = signalScore + patternScore;

  const [infoSignal, setInfoSignal] = useState<string | null>(null);
  const [alertThreshold, setAlertThreshold] = useState<number>(loadAlertThreshold);
  const [showAlertConfig, setShowAlertConfig] = useState(false);
  const lastAlertRef = useRef<string | null>(null);
  // Score history for sparkline — accumulate last 20 values
  const scoreHistoryRef = useRef<number[]>([]);

  // BB Squeeze flag — drives volatile strategy override
  const bbSqueeze = useMemo(() => {
    const bbU = tech?.bollinger_bands?.upper;
    const bbL = tech?.bollinger_bands?.lower;
    const bbM = tech?.bollinger_bands?.middle;
    if (!bbU || !bbL || !bbM || bbM <= 0) return false;
    return ((bbU - bbL) / bbM) * 100 < 1.5;
  }, [tech]);

  const strategies = useMemo(
    () => selectStrategies(score, adx, adxSig, atrSig, spotPrice, maxPain, oiLevels, step, bbSqueeze),
    [score, adx, adxSig, atrSig, spotPrice, maxPain, oiLevels, step, bbSqueeze]
  );

  // Emit signal change when score crosses ±7 threshold (once per crossing)
  const lastSignalTypeRef = useRef<'CE' | 'PE' | null>(null);
  useEffect(() => {
    if (!onSignalChange) return;
    const newType: 'CE' | 'PE' | null = score >= 7 ? 'CE' : score <= -7 ? 'PE' : null;
    if (newType === lastSignalTypeRef.current) return;
    lastSignalTypeRef.current = newType;
    onSignalChange(newType ? { type: newType, score } : null);
  }, [score, onSignalChange]);

  // Accumulate score history for sparkline
  useEffect(() => {
    if (spotPrice <= 0) return;
    scoreHistoryRef.current = [...scoreHistoryRef.current, score].slice(-20);
  }, [score, spotPrice]);

  // Browser alert when score crosses user-set threshold
  useEffect(() => {
    if (!spotPrice) return;
    const thr = alertThreshold;
    const alertKey = score >= thr ? `bull_${thr}` : score <= -thr ? `bear_${thr}` : null;
    if (alertKey && alertKey !== lastAlertRef.current) {
      lastAlertRef.current = alertKey;
      fireNotification(
        `${symbol} Signal Alert`,
        score > 0
          ? `Strong BULL signal: score +${score}/${signals.length}. Consider CE strategy.`
          : `Strong BEAR signal: score ${score}/${signals.length}. Consider PE strategy.`
      );
    }
    if (!alertKey) lastAlertRef.current = null;
  }, [score, alertThreshold, symbol, signals.length, spotPrice]);

  // ATM straddle premium computation
  const atmStraddle = useMemo(() => {
    if (!optionChain.length || spotPrice <= 0) return null;
    const atmStrike = atm(spotPrice, step);
    const row = optionChain.reduce<OptionRow | null>((best, r) =>
      !best || Math.abs(r.strike - atmStrike) < Math.abs(best.strike - atmStrike) ? r : best, null);
    if (!row || row.callLTP <= 0 || row.putLTP <= 0) return null;
    const premium = row.callLTP + row.putLTP;
    const pct = (premium / spotPrice) * 100;
    return { strike: row.strike, ce: row.callLTP, pe: row.putLTP, premium, pct };
  }, [optionChain, spotPrice, step]);

  if (!tech || spotPrice <= 0) {
    return (
      <div style={{ padding: '0.6rem', fontSize: '0.68rem', color: '#555', background: '#0d1117', borderTop: '1px solid #1e222d' }}>
        Strategy signals require technical indicators + live price
      </div>
    );
  }

  const bullCount = signals.filter(s => s.score > 0).length;
  const bearCount = signals.filter(s => s.score < 0).length;
  const totalSig = signals.length;

  // Thresholds scaled to 14-signal range (max ±13)
  const biasLabel =
    score >= 6 ? 'STRONG BULL' :
    score >= 3 ? 'BULL' :
    score <= -6 ? 'STRONG BEAR' :
    score <= -3 ? 'BEAR' :
    score === 0 ? 'NEUTRAL' :
    score > 0 ? 'MILD BULL' : 'MILD BEAR';
  const biasColor = score >= 3 ? '#26a69a' : score <= -3 ? '#ef5350' : '#ffd54f';

  const typeColor = (t: Strategy['type']) =>
    t === 'bull' ? '#26a69a' : t === 'bear' ? '#ef5350' : t === 'volatile' ? '#4fc3f7' : '#9e9e9e';

  const tagBg = (tag: string) => {
    if (tag === 'BEST FIT' || tag === 'MOMENTUM') return '#1a3a1a';
    if (tag === 'CONTEXT PLAY' || tag === 'EXPIRY PLAY') return '#1a2e3a';
    if (tag === 'PREMIUM') return '#2a2400';
    if (tag === 'SAFEST') return '#1a2535';
    if (tag === 'BREAKOUT') return '#0d1f3a';
    return '#1e222d';
  };

  // Score bar: ±13 max range → 0–100%
  const scoreBarWidth = ((score + 13) / 26) * 100;
  const scoreBarColor = score >= 3 ? '#26a69a' : score <= -3 ? '#ef5350' : '#ffd54f';

  // Sparkline SVG for score history
  const sparkline = (() => {
    const hist = scoreHistoryRef.current;
    if (hist.length < 2) return null;
    const W = 64, H = 18;
    const min = Math.min(-3, ...hist), max = Math.max(3, ...hist);
    const range = max - min || 1;
    const pts = hist.map((v, i) => {
      const x = (i / (hist.length - 1)) * W;
      const y = H - ((v - min) / range) * H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    const zeroY = H - ((0 - min) / range) * H;
    return (
      <svg width={W} height={H} style={{ display: 'block', flexShrink: 0 }}>
        <line x1={0} y1={zeroY.toFixed(1)} x2={W} y2={zeroY.toFixed(1)} stroke="#2b2b43" strokeWidth={1} />
        <polyline points={pts} fill="none" stroke={scoreBarColor} strokeWidth={1.5} />
        <circle cx={(W).toFixed(1)} cy={(H - ((hist[hist.length - 1] - min) / range) * H).toFixed(1)} r={2} fill={scoreBarColor} />
      </svg>
    );
  })();

  return (
    <div style={{ background: '#0d1117', borderTop: '2px solid #2b2b43', fontSize: '0.7rem' }}>
      {infoSignal && <SignalInfoModal name={infoSignal} onClose={() => setInfoSignal(null)} />}
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.6rem', background: '#131722', borderBottom: '1px solid #1e222d', flexWrap: 'wrap' }}>
        <span style={{ color: '#787b86', fontWeight: 600, fontSize: '0.65rem', letterSpacing: 0.8 }}>STRATEGY SIGNAL</span>
        <span style={{ color: biasColor, fontWeight: 800, fontSize: '0.72rem' }}>{biasLabel}</span>
        <span style={{ color: '#555', fontSize: '0.6rem' }}>
          {bullCount}↑ {bearCount}↓ of {totalSig}
        </span>

        {/* Score bar */}
        <div style={{ flex: 1, minWidth: 80, height: 6, background: '#1e222d', borderRadius: 3, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: '#363a45' }} />
          <div style={{
            position: 'absolute',
            left: score < 0 ? `${scoreBarWidth}%` : '50%',
            width: `${Math.abs(score / 26) * 100}%`,
            top: 0, bottom: 0, background: scoreBarColor, borderRadius: 3,
          }} />
        </div>

        <span style={{ color: biasColor, fontWeight: 700, fontFamily: 'monospace', fontSize: '0.68rem' }}>
          {score > 0 ? '+' : ''}{score}/{totalSig}{patternScore !== 0 ? <span style={{ color: '#555', fontWeight: 400 }}>{patternScore > 0 ? '+P' : '-P'}</span> : null}
        </span>

        {/* Score history sparkline */}
        {sparkline}

        <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.58rem', color: '#555' }}>
          <span>ADX <span style={{ color: adx > 25 ? '#ffd54f' : '#555' }}>{adx ? adx.toFixed(0) : '—'}</span></span>
          <span>ATR <span style={{ color: atrSig === 'high' ? '#ef5350' : atrSig === 'low' ? '#26a69a' : '#787b86' }}>{atrSig || '—'}</span></span>
          {bbSqueeze && <span style={{ color: '#4fc3f7', fontWeight: 700 }}>⚡SQUEEZE</span>}
          {/* Alert config toggle */}
          <button
            onClick={() => { requestNotificationPermission(); setShowAlertConfig(v => !v); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.58rem', color: showAlertConfig ? '#ffd54f' : '#444' }}
            title="Configure alerts"
          >🔔</button>
        </div>
      </div>

      {/* Alert config row */}
      {showAlertConfig && (
        <div style={{ padding: '0.3rem 0.6rem', background: '#0d1117', borderBottom: '1px solid #1e222d', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.6rem' }}>
          <span style={{ color: '#787b86' }}>Alert when score reaches ±</span>
          <input
            type="number" min={3} max={12} value={alertThreshold}
            onChange={e => { const v = Math.max(3, Math.min(12, parseInt(e.target.value) || 7)); setAlertThreshold(v); saveAlertThreshold(v); }}
            style={{ width: 36, background: '#1e222d', border: '1px solid #363a45', color: '#d1d4dc', borderRadius: 3, padding: '1px 4px', fontSize: '0.6rem', textAlign: 'center' }}
          />
          <span style={{ color: '#555' }}>
            {Notification && Notification.permission === 'granted' ? '✅ Notifications on' :
             Notification && Notification.permission === 'denied' ? '❌ Blocked in browser' :
             '⚠ Click 🔔 to enable'}
          </span>
        </div>
      )}

      {/* ATM Straddle Premium */}
      {atmStraddle && (
        <div style={{ padding: '0.3rem 0.6rem', background: '#131722', borderBottom: '1px solid #1e222d', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.6rem', flexWrap: 'wrap' }}>
          <span style={{ color: '#787b86' }}>ATM {atmStraddle.strike} Straddle:</span>
          <span>CE <span style={{ color: '#26a69a', fontFamily: 'monospace' }}>₹{atmStraddle.ce.toFixed(0)}</span></span>
          <span>PE <span style={{ color: '#ef5350', fontFamily: 'monospace' }}>₹{atmStraddle.pe.toFixed(0)}</span></span>
          <span style={{ color: '#d1d4dc', fontWeight: 700, fontFamily: 'monospace' }}>= ₹{Math.round(atmStraddle.premium)}</span>
          <span style={{ color: atmStraddle.pct > 2 ? '#ef5350' : atmStraddle.pct < 0.8 ? '#26a69a' : '#ffd54f', fontWeight: 700 }}>
            {atmStraddle.pct.toFixed(1)}%
          </span>
          <span style={{ color: '#555' }}>
            {atmStraddle.pct > 2 ? '← expensive · sell' : atmStraddle.pct < 0.8 ? '← cheap · buy' : '← fair value'}
          </span>
        </div>
      )}

      {/* ── Chart Patterns ── */}
      {chartPatterns.length > 0 && (
        <div style={{ padding: '0.3rem 0.6rem', background: '#0d1117', borderBottom: '1px solid #1e222d', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '0.55rem', color: '#555', textTransform: 'uppercase', letterSpacing: 0.8, flexShrink: 0 }}>Patterns</span>
          {chartPatterns.map((p, i) => {
            const bull = p.signal === 'bull';
            const color = bull ? '#4fc3f7' : '#FF9800';
            const bg = bull ? '#0a1e2e' : '#2a1a00';
            return (
              <div
                key={i}
                title={p.description}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: bg, border: `1px solid ${color}44`,
                  borderRadius: 3, padding: '2px 6px', cursor: 'help',
                }}
              >
                <span style={{ color, fontSize: '0.6rem', fontWeight: 700 }}>
                  {bull ? '▲' : '▼'} {PATTERN_FULL_NAME[p.type]}
                </span>
                <span style={{ fontSize: '0.55rem', color: color + 'aa' }}>
                  {p.confidence}%{p.confirmed ? ' ✓' : '?'}
                </span>
                <span style={{ fontSize: '0.55rem', color: bull ? '#26a69a' : '#ef5350', fontWeight: 700 }}>
                  {bull ? `+${p.scoreContribution}` : `-${p.scoreContribution}`}
                </span>
              </div>
            );
          })}
          {patternScore !== 0 && (
            <span style={{ marginLeft: 'auto', fontSize: '0.58rem', color: patternScore > 0 ? '#26a69a' : '#ef5350', fontWeight: 700 }}>
              Pattern adj: {patternScore > 0 ? '+' : ''}{patternScore}
            </span>
          )}
        </div>
      )}

      {/* ── Body: signals left, strategies right ── */}
      <div style={{ display: 'flex', gap: 0 }}>
        {/* Signal Breakdown */}
        <div style={{ width: '44%', padding: '0.4rem 0.5rem', borderRight: '1px solid #1e222d' }}>
          {signals.map((sig, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '1.5px 0', borderBottom: '1px solid #0d1117' }}>
              <span style={{ fontSize: '0.6rem', flexShrink: 0 }}>
                {sig.score > 0
                  ? <span style={{ color: '#26a69a' }}>✅</span>
                  : sig.score < 0
                    ? <span style={{ color: '#ef5350' }}>❌</span>
                    : <span style={{ color: '#555' }}>⬜</span>}
              </span>
              <button
                onClick={() => setInfoSignal(sig.name)}
                style={{ color: '#787b86', fontSize: '0.58rem', flexShrink: 0, minWidth: 56, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
              >{sig.name}</button>
              <span style={{ flex: 1, overflow: 'hidden' }}>
                <span style={{
                  color: sig.score > 0 ? '#26a69a' : sig.score < 0 ? '#ef5350' : '#555',
                  fontSize: '0.6rem', whiteSpace: 'nowrap',
                }}>{sig.value}</span>
                {sig.thr && (
                  <span style={{ color: '#2e3347', fontSize: '0.5rem', marginLeft: 4, whiteSpace: 'nowrap' }}>[{sig.thr}]</span>
                )}
              </span>
            </div>
          ))}
        </div>

        {/* Strategy Recommendations */}
        <div style={{ flex: 1, padding: '0.4rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {strategies.length === 0 ? (
            <div style={{ color: '#555', fontSize: '0.65rem', textAlign: 'center', padding: '0.5rem' }}>
              No clear strategy — wait for confirmation
            </div>
          ) : (
            strategies.map((st, i) => (
              <div key={i} style={{ background: '#131722', border: `1px solid ${typeColor(st.type)}22`, borderRadius: 4, padding: '0.4rem 0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem' }}>
                  <span style={{ color: typeColor(st.type), fontWeight: 700, fontSize: '0.7rem' }}>{st.name}</span>
                  <span style={{
                    fontSize: '0.52rem', fontWeight: 700, padding: '1px 5px', borderRadius: 2,
                    background: tagBg(st.tag), color: typeColor(st.type), letterSpacing: 0.5,
                  }}>{st.tag}</span>
                  {i === 0 && strategies.length > 1 && (
                    <span style={{ fontSize: '0.52rem', color: '#ffd54f', marginLeft: 'auto' }}>★ Preferred</span>
                  )}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.2rem' }}>
                  {st.legs.map((leg, j) => (
                    <span key={j} style={{
                      background: '#1e222d', border: `1px solid ${typeColor(st.type)}44`,
                      color: typeColor(st.type), fontSize: '0.62rem', padding: '1px 6px', borderRadius: 3, fontFamily: 'monospace',
                    }}>{leg}</span>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.58rem' }}>
                  <span><span style={{ color: '#555' }}>SL: </span><span style={{ color: '#ef5350' }}>{st.sl}</span></span>
                  <span><span style={{ color: '#555' }}>Tgt: </span><span style={{ color: '#26a69a' }}>{st.target}</span></span>
                </div>

                {/* Entry setup: premium-based SL & target */}
                {atmStraddle && (st.type === 'bull' || st.type === 'bear') && (() => {
                  const leg1Premium = st.type === 'bull' ? atmStraddle.ce : atmStraddle.pe;
                  const slPts = Math.round(leg1Premium * 0.4);
                  const tgtPts = Math.round(leg1Premium * 0.8);
                  const rr = (tgtPts / slPts).toFixed(1);
                  return (
                    <div style={{ marginTop: '0.2rem', display: 'flex', gap: '0.5rem', fontSize: '0.56rem', flexWrap: 'wrap' }}>
                      <span style={{ color: '#555' }}>Premium:</span>
                      <span style={{ color: '#d1d4dc', fontFamily: 'monospace' }}>₹{leg1Premium.toFixed(0)}</span>
                      <span style={{ color: '#555' }}>SL:</span>
                      <span style={{ color: '#ef5350', fontFamily: 'monospace' }}>−₹{slPts}</span>
                      <span style={{ color: '#555' }}>Tgt:</span>
                      <span style={{ color: '#26a69a', fontFamily: 'monospace' }}>+₹{tgtPts}</span>
                      <span style={{ color: '#555' }}>R:R</span>
                      <span style={{ color: '#ffd54f', fontFamily: 'monospace' }}>1:{rr}</span>
                    </div>
                  );
                })()}

                <div style={{ marginTop: '0.2rem', color: '#555', fontSize: '0.58rem', fontStyle: 'italic', lineHeight: 1.3 }}>
                  {st.rationale}
                </div>
              </div>
            ))
          )}

          <div style={{ fontSize: '0.56rem', color: '#444', textAlign: 'right', marginTop: 'auto', paddingTop: '0.2rem' }}>
            ATM: {atm(spotPrice, step)} · step: {step} · No AI
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategySignalPanel;
